import React, { useState, useEffect } from 'react';
import axios from 'axios';
import ConfirmationModal from '../ui/ConfirmationModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OAuthProviderSettings = ({ adminToken }) => {
  const [settings, setSettings] = useState({
    oauth_provider: 'emergent',
    custom_provider_name: '3stech.ai',
    custom_client_configured: false
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    targetProvider: null
  });

  useEffect(() => {
    fetchSettings();
  }, [adminToken]);

  const fetchSettings = async () => {
    if (!adminToken) return;
    
    try {
      const response = await axios.get(`${API}/admin/system-settings/oauth-provider`, {
        headers: { Authorization: `Bearer ${adminToken}` }
      });
      setSettings(response.data);
    } catch (err) {
      console.error('Error fetching OAuth settings:', err);
      setMessage({ type: 'error', text: 'Failed to load OAuth settings' });
    } finally {
      setLoading(false);
    }
  };

  const handleProviderToggle = (newProvider) => {
    if (newProvider === settings.oauth_provider) return;
    
    // Show confirmation modal
    setConfirmModal({
      isOpen: true,
      targetProvider: newProvider
    });
  };

  const executeProviderChange = async () => {
    const newProvider = confirmModal.targetProvider;
    setSaving(true);
    setMessage(null);
    setConfirmModal({ isOpen: false, targetProvider: null });

    try {
      const response = await axios.put(
        `${API}/admin/system-settings/oauth-provider`,
        {
          oauth_provider: newProvider,
          custom_provider_name: settings.custom_provider_name
        },
        { headers: { Authorization: `Bearer ${adminToken}` }}
      );

      setSettings(prev => ({ ...prev, oauth_provider: newProvider }));
      setMessage({
        type: 'success',
        text: `✅ OAuth provider switched to ${newProvider === 'custom' ? settings.custom_provider_name : 'Emergent'}`
      });

      setTimeout(() => setMessage(null), 5000);
    } catch (err) {
      setMessage({
        type: 'error',
        text: err.response?.data?.detail || 'Failed to update OAuth provider'
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-4">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-600"></div>
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-6">
      {/* Confirmation Modal */}
      <ConfirmationModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, targetProvider: null })}
        onConfirm={executeProviderChange}
        title="Switch OAuth Provider?"
        message={
          <div>
            <p className="mb-3">You are about to switch the Google Sign-In provider.</p>
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-lg p-3 mb-3">
              <p className="text-yellow-700 dark:text-yellow-300 font-medium">
                ⚠️ This will affect all users trying to sign in with Google
              </p>
            </div>
            <p>
              <strong>Current:</strong> {settings.oauth_provider === 'custom' ? settings.custom_provider_name : 'Emergent'}<br/>
              <strong>New:</strong> {confirmModal.targetProvider === 'custom' ? settings.custom_provider_name : 'Emergent'}
            </p>
          </div>
        }
        type="warning"
        icon="🔐"
        confirmText="Switch Provider"
        cancelText="Cancel"
        isProcessing={saving}
      />

      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
          🔐 Google OAuth Provider
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          Choose which Google Sign-In provider to use for authentication
        </p>
      </div>

      {/* Message */}
      {message && (
        <div className={`mb-4 p-4 rounded-lg ${
          message.type === 'success' 
            ? 'bg-green-50 dark:bg-green-900/20 border border-green-500 text-green-700 dark:text-green-300' 
            : 'bg-red-50 dark:bg-red-900/20 border border-red-500 text-red-700 dark:text-red-300'
        }`}>
          {message.text}
        </div>
      )}

      {/* Provider Selection */}
      <div className="space-y-4">
        {/* Emergent Option */}
        <div
          onClick={() => handleProviderToggle('emergent')}
          className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
            settings.oauth_provider === 'emergent'
              ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
              : 'border-gray-200 dark:border-gray-600 hover:border-blue-300'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                settings.oauth_provider === 'emergent'
                  ? 'border-blue-500 bg-blue-500'
                  : 'border-gray-300'
              }`}>
                {settings.oauth_provider === 'emergent' && (
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  🛡️ Emergent (Default)
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Managed Google OAuth via Emergent Auth
                </p>
              </div>
            </div>
            {settings.oauth_provider === 'emergent' && (
              <span className="text-xs bg-blue-100 dark:bg-blue-900 text-blue-700 dark:text-blue-300 px-2 py-1 rounded-full">
                Active
              </span>
            )}
          </div>
          <div className="mt-2 ml-7 text-xs text-gray-500 dark:text-gray-400">
            • No configuration needed<br/>
            • Managed by Emergent platform<br/>
            • Shows "Secured by Emergent" branding
          </div>
        </div>

        {/* Custom Option */}
        <div
          onClick={() => settings.custom_client_configured && handleProviderToggle('custom')}
          className={`p-4 rounded-lg border-2 transition-all ${
            !settings.custom_client_configured
              ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900 opacity-60 cursor-not-allowed'
              : settings.oauth_provider === 'custom'
                ? 'border-green-500 bg-green-50 dark:bg-green-900/20 cursor-pointer'
                : 'border-gray-200 dark:border-gray-600 hover:border-green-300 cursor-pointer'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${
                settings.oauth_provider === 'custom'
                  ? 'border-green-500 bg-green-500'
                  : 'border-gray-300'
              }`}>
                {settings.oauth_provider === 'custom' && (
                  <div className="w-2 h-2 rounded-full bg-white"></div>
                )}
              </div>
              <div>
                <p className="font-medium text-gray-900 dark:text-white">
                  🏢 Custom ({settings.custom_provider_name})
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Your own Google Cloud OAuth credentials
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {settings.custom_client_configured ? (
                <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                  Configured
                </span>
              ) : (
                <span className="text-xs bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 px-2 py-1 rounded-full">
                  Not Configured
                </span>
              )}
              {settings.oauth_provider === 'custom' && (
                <span className="text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">
                  Active
                </span>
              )}
            </div>
          </div>
          <div className="mt-2 ml-7 text-xs text-gray-500 dark:text-gray-400">
            • Uses your Google Cloud project<br/>
            • Custom branding on consent screen<br/>
            • Shows "Secured by {settings.custom_provider_name}" branding
          </div>
        </div>
      </div>

      {/* Info Box */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <div className="flex items-start">
          <span className="text-xl mr-3">💡</span>
          <div>
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
              About OAuth Providers
            </h3>
            <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
              <strong>Emergent:</strong> Quick setup, managed service, shows Emergent branding.<br/>
              <strong>Custom:</strong> Full control, your branding, requires Google Cloud setup.
            </p>
            <p className="mt-2 text-sm text-blue-700 dark:text-blue-300">
              You can switch between providers anytime. Existing users will still be able to log in.
            </p>
          </div>
        </div>
      </div>

      {/* Configuration Status */}
      {settings.custom_client_configured && (
        <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Custom OAuth Configuration
          </h4>
          <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
            <p>✅ Google Client ID configured</p>
            <p>✅ Google Client Secret configured</p>
            <p>📍 Redirect URI: {window.location.origin}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OAuthProviderSettings;
